import { useTranslation } from 'react-i18next';

export default function OverseasStrengthsSection() {
  const { t } = useTranslation();

  const strengths = [
    {
      icon: 'ri-home-heart-line',
      title: 'Proven Track Record',
      description: 'We don\'t just manage properties—we operate our own successful vacation rentals in Tokyo. We understand the business from an owner\'s perspective.',
      stats: '150+ properties managed',
      image: 'https://public.readdy.ai/ai/img_res/b654a7b3d7324a4c41855cba79505b0c.jpg'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Tokyo Area Specialist',
      description: 'Deep local expertise in Tokyo\'s vacation rental market, regulations, and guest preferences. We know what works in each neighborhood.',
      stats: 'All 23 wards covered',
      image: 'https://readdy.ai/api/search-image?query=Tokyo%20cityscape%20showing%20diverse%20neighborhoods%20from%20Shibuya%20to%20Shinjuku%20urban%20landscape%20with%20mix%20of%20modern%20and%20traditional%20areas%20representing%20comprehensive%20local%20market%20knowledge%20and%20expertise&width=800&height=600&seq=overseas-strength-2&orientation=landscape'
    },
    {
      icon: 'ri-global-line',
      title: 'Full English Support',
      description: 'Seamless communication in English for all aspects of your business. No language barriers, no miscommunication, just clear and professional service.',
      stats: 'Native-level English',
      image: 'https://readdy.ai/api/search-image?query=Professional%20international%20business%20communication%20with%20laptop%20showing%20English%20reports%20and%20documents%20modern%20office%20environment%20representing%20bilingual%20support%20and%20global%20business%20operations&width=800&height=600&seq=overseas-strength-3&orientation=landscape'
    },
    {
      icon: 'ri-file-chart-line',
      title: 'Tax Expertise',
      description: 'Specialized knowledge in non-resident taxation and Japanese tax regulations. We ensure full compliance while optimizing your tax position.',
      stats: '100% compliance rate',
      image: 'https://readdy.ai/api/search-image?query=Professional%20tax%20documents%20and%20financial%20reports%20on%20desk%20with%20calculator%20organized%20paperwork%20representing%20expert%20tax%20filing%20services%20and%20financial%20compliance%20for%20international%20property%20owners&width=800&height=600&seq=overseas-strength-4&orientation=landscape'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Transparent Reporting',
      description: 'Detailed monthly reports in English covering revenue, expenses, occupancy, and performance metrics. Always know exactly how your property is performing.',
      stats: 'Monthly detailed reports',
      image: 'https://readdy.ai/api/search-image?query=Modern%20business%20analytics%20dashboard%20on%20computer%20screen%20showing%20graphs%20charts%20and%20performance%20metrics%20clean%20professional%20interface%20representing%20transparent%20monthly%20reporting%20and%20data%20visualization&width=800&height=600&seq=overseas-strength-5&orientation=landscape'
    }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-full mb-4">
            WHY CHOOSE US
          </div>
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            Your Success is Our Priority
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We combine local expertise, proven experience, and international standards to deliver exceptional results for overseas property owners.
          </p>
        </div>

        <div className="space-y-16">
          {strengths.map((strength, index) => (
            <div
              key={index}
              className={`flex flex-col ${
                index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              } gap-12 items-center`}
            >
              {/* Image */}
              <div className="w-full lg:w-1/2">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                  <img
                    src={strength.image}
                    alt={strength.title}
                    className="w-full h-[400px] object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm px-6 py-3 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">{strength.stats}</div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="w-full lg:w-1/2">
                <div className="w-16 h-16 bg-gradient-to-br from-gray-900 to-gray-700 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${strength.icon} text-3xl text-white`}></i>
                </div>

                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  {strength.title}
                </h3>

                <p className="text-lg text-gray-600 leading-relaxed">
                  {strength.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="text-4xl font-bold text-gray-900 mb-2">150+</div>
            <div className="text-sm text-gray-600 uppercase tracking-wider">Properties Managed</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-gray-900 mb-2">98%</div>
            <div className="text-sm text-gray-600 uppercase tracking-wider">Client Satisfaction</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-gray-900 mb-2">5+ Years</div>
            <div className="text-sm text-gray-600 uppercase tracking-wider">Industry Experience</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-gray-900 mb-2">24/7</div>
            <div className="text-sm text-gray-600 uppercase tracking-wider">Support Available</div>
          </div>
        </div>
      </div>
    </section>
  );
}